import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Check, Circle, Clock } from 'lucide-react';
import {
  MessagingProvisioningStatus,
  RecipientSource,
  WorkOrderStatus,
  assertNever,
  registrationLabel,
  type CommitteeFactsDto,
  type RegistrationStatusDto,
  type TextSendStatusDto,
  type WorkOrderDto,
} from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { REGISTRATION_WAIT_DAYS, describeSendTime } from './workOrderSchedule';

/** Where one paid Text send stands, whoever is asking: the status link or the signed-in detail. */
export interface SendProgress {
  status: WorkOrderStatus;
  /** Null when the send doesn't wait on carrier registration. */
  registration: RegistrationStatusDto | null;
  /** Null unless the Recipient list is bought. */
  boughtList: { ready: boolean } | null;
  sendDate: string | null;
  sendTime: string | null;
  recipientCount: number | null;
  /** The results report is attached; only a Complete send has one. */
  hasResultsReport: boolean;
}

const PAID: readonly WorkOrderStatus[] = [
  WorkOrderStatus.Paid,
  WorkOrderStatus.SentToVendor,
  WorkOrderStatus.VendorPaid,
  WorkOrderStatus.Complete,
];
const HANDED_OFF: readonly WorkOrderStatus[] = [WorkOrderStatus.SentToVendor, WorkOrderStatus.VendorPaid, WorkOrderStatus.Complete];

export function progressOfStatusLink(link: TextSendStatusDto): SendProgress {
  return {
    status: link.status,
    registration: link.registration,
    boughtList: link.hasBoughtList ? { ready: link.listReady } : null,
    sendDate: link.sendDate,
    sendTime: link.sendTime,
    recipientCount: link.recipientCount,
    hasResultsReport: link.hasResultsReport,
  };
}

/**
 * The same progress for a signed-in campaign, from its Work Order, its committee read and the Voter
 * data order its list comes from. The list is ready once that order's file is delivered.
 */
export function progressOfWorkOrder(
  workOrder: WorkOrderDto,
  committee: CommitteeFactsDto | null,
  pairedOrder: WorkOrderDto | null,
): SendProgress {
  const bought = workOrder.details.recipientSource === RecipientSource.BoughtList;
  return {
    status: workOrder.status,
    registration:
      committee && !workOrder.skipMessagingProfile ? { status: committee.status, rejectionReason: committee.rejectionReason } : null,
    boughtList: bought
      ? { ready: HANDED_OFF.includes(workOrder.status) || !!pairedOrder?.voterData?.deliveredAt }
      : null,
    sendDate: workOrder.sendDate,
    sendTime: workOrder.sendTime,
    recipientCount: workOrder.details.recipientCount ?? workOrder.recipientCount,
    hasResultsReport: workOrder.details.hasResultsReport,
  };
}

type RowState = 'done' | 'active' | 'waiting' | 'problem';

interface Row {
  key: string;
  state: RowState;
  title: string;
  detail: string;
  fix?: boolean;
}

function registrationRow({ status, rejectionReason }: RegistrationStatusDto): Row {
  const title = `Registration: ${registrationLabel(status)}`;
  switch (status) {
    case MessagingProvisioningStatus.NotStarted:
      return { key: 'registration', state: 'problem', title, detail: 'We need your committee details to register you.', fix: true };
    case MessagingProvisioningStatus.InfoCollected:
    case MessagingProvisioningStatus.SubmittedToVendor:
      return { key: 'registration', state: 'active', title, detail: `About ${REGISTRATION_WAIT_DAYS} days. We email you when it changes.` };
    case MessagingProvisioningStatus.Provisioned:
      return { key: 'registration', state: 'done', title, detail: 'Your committee is cleared to text.' };
    case MessagingProvisioningStatus.Rejected:
    case MessagingProvisioningStatus.Failed:
      return {
        key: 'registration',
        state: 'problem',
        title,
        detail: rejectionReason ?? 'Carriers need a change to your committee details.',
        fix: true,
      };
    default:
      return assertNever(status);
  }
}

function rowsOf(progress: SendProgress): Row[] {
  const paid = PAID.includes(progress.status);
  const handedOff = HANDED_OFF.includes(progress.status);
  const when = describeSendTime(progress.sendDate, progress.sendTime) ?? 'your send date';
  const rows: Row[] = [];

  if (progress.status === WorkOrderStatus.Cancelled) {
    rows.push({ key: 'paid', state: 'problem', title: 'Cancelled', detail: 'This text won’t go out.' });
  } else if (progress.status === WorkOrderStatus.Failed) {
    rows.push({ key: 'paid', state: 'problem', title: 'Something went wrong', detail: 'We’ll contact you about this text.' });
  } else {
    rows.push({
      key: 'paid',
      state: paid ? 'done' : 'waiting',
      title: paid ? 'Paid' : 'Not paid yet',
      detail: paid ? 'Your receipt is in your email.' : 'Nothing happens until it’s paid.',
    });
  }
  if (progress.registration) rows.push(registrationRow(progress.registration));
  if (progress.boughtList) {
    const { ready } = progress.boughtList;
    rows.push({
      key: 'list',
      state: ready ? 'done' : 'active',
      title: ready ? 'Voter list: Ready' : 'Voter list: Being pulled',
      detail: ready ? 'Your list is ready to text.' : 'We’re pulling your list from the voter file.',
    });
  }
  rows.push({
    key: 'vendor',
    state: handedOff ? 'done' : 'waiting',
    title: 'Sent to our texting partner',
    detail: handedOff ? `Goes out ${when}.` : `Goes out ${when}, once everything above is done.`,
  });
  const delivered = progress.status === WorkOrderStatus.Complete;
  rows.push({
    key: 'delivered',
    state: delivered ? 'done' : 'waiting',
    title: 'Delivered',
    detail: delivered ? 'Your text went out.' : 'We confirm when it has gone out.',
  });
  if (delivered) {
    rows.push({
      key: 'results',
      state: progress.hasResultsReport ? 'done' : 'waiting',
      title: 'Results report',
      detail: progress.hasResultsReport ? 'Sent, delivered, replies and opt-outs, in one PDF.' : 'Your results report is on its way.',
    });
  }
  return rows;
}

const ICONS: Record<RowState, ReactNode> = {
  done: <Check className="h-4 w-4 text-primary-foreground" aria-hidden="true" />,
  active: <Clock className="h-4 w-4 text-primary" aria-hidden="true" />,
  waiting: <Circle className="h-3 w-3 text-muted-foreground" aria-hidden="true" />,
  problem: <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />,
};

/**
 * Where a paid Text send is, row by row: paid, registration, a bought list, the handoff, delivery.
 * Fixing turned-down details happens in the Express committee step, behind sign-in for the status link.
 */
export function SendChecklist({ progress, signedIn }: { progress: SendProgress; signedIn: boolean }) {
  return (
    <ol aria-label="Where your text is" className="space-y-1">
      {rowsOf(progress).map((row) => (
        <li key={row.key} className="flex gap-3">
          <span
            className={cn(
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border',
              row.state === 'done' && 'border-primary bg-primary',
              row.state === 'active' && 'border-primary',
              row.state === 'problem' && 'border-destructive',
            )}
          >
            {ICONS[row.state]}
          </span>
          <div className="min-w-0 flex-1 pb-4">
            <p className={cn('font-medium', row.state === 'waiting' && 'text-muted-foreground')}>{row.title}</p>
            <p className="text-sm text-muted-foreground">{row.detail}</p>
            {row.fix && (
              <Link
                to="/express/send"
                search={{ step: 'registration' }}
                className="mt-2 inline-block text-sm font-semibold text-primary underline-offset-2 hover:underline"
              >
                {signedIn ? 'Fix your details' : 'Sign in to fix your details'}
              </Link>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
